import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Tab, Nav, InputGroup } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import Orders from './Orders';
import { toast } from 'react-toastify';
import api from '../config/api';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './Profile.css';

// Telefon numarasını formatla
const formatPhoneNumber = (phoneNumber) => {
  // Sadece rakamları al
  const numbers = phoneNumber.replace(/\D/g, '');
  
  // Eğer numara 10 haneli değilse orijinal değeri döndür
  if (numbers.length !== 10) return phoneNumber;
  
  // XXX XXX XXXX formatında düzenle
  return numbers.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3');
};

// Telefon numarasından formatı kaldır
const unformatPhoneNumber = (phoneNumber) => {
  return phoneNumber.replace(/\D/g, '');
};

function Profile() {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone ? formatPhoneNumber(user.phone.replace('+90', '')) : '',
  });

  const [passwordInfo, setPasswordInfo] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const handleInfoUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.put(
        '/users/update-profile',
        {
          first_name: userInfo.firstName,
          last_name: userInfo.lastName,
          phone: unformatPhoneNumber(userInfo.phone)
        }
      );

      if (response?.status === 200 && response.data) {
        // Güncellenmiş kullanıcı bilgilerini localStorage'a kaydet
        const updatedUser = {
          ...user,
          first_name: response.data.first_name,
          last_name: response.data.last_name,
          phone: response.data.phone
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Context'teki user bilgisini güncelle
        setUser(updatedUser);

        // Form verilerini güncelle
        setUserInfo({
          firstName: response.data.first_name,
          lastName: response.data.last_name,
          email: user.email,
          phone: formatPhoneNumber(response.data.phone.replace(/^\+90/, ''))
        });

        toast.success('Profil bilgileriniz başarıyla güncellendi.');
      } else {
        throw new Error('API yanıtı beklenen formatta değil');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || 
        'Profil bilgileriniz güncellenirken bir hata oluştu.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');
    
    // Maksimum 10 rakam olacak şekilde sınırla
    if (numbers.length <= 10) {
      setUserInfo(prev => ({
        ...prev,
        phone: formatPhoneNumber(numbers)
      }));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (passwordInfo.newPassword !== passwordInfo.confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor.');
      setLoading(false);
      return;
    }

    try {
      const response = await api.put('/users/change-password', {
        current_password: passwordInfo.currentPassword,
        new_password: passwordInfo.newPassword
      });

      if (response?.status === 200) {
        toast.success('Şifreniz başarıyla güncellendi.');
        setPasswordInfo({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || 
        'Şifreniz güncellenirken bir hata oluştu.'
      );
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <Container className="profile-page page-transition">
      <h2 className="profile-title">Hesabım</h2>
      <Tab.Container defaultActiveKey="profile">
        <Row>
          <Col md={3}>
            <div className="mb-4">
              <Nav variant="pills" className="flex-column">
                <Nav.Item>
                  <Nav.Link eventKey="profile">
                    <i className="fas fa-user me-2"></i>
                    Profil Bilgileri
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="orders">
                    <i className="fas fa-shopping-bag me-2"></i>
                    Sipariş Geçmişi
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="password">
                    <i className="fas fa-lock me-2"></i>
                    Şifremi Değiştir
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
          </Col>
          <Col md={9}>
            <Tab.Content>
              <Tab.Pane eventKey="profile">
                <Form onSubmit={handleInfoUpdate}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Ad</Form.Label>
                        <Form.Control
                          type="text"
                          value={userInfo.firstName}
                          onChange={(e) => setUserInfo({...userInfo, firstName: e.target.value})}
                          required
                          placeholder="Adınız"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Soyad</Form.Label>
                        <Form.Control
                          type="text"
                          value={userInfo.lastName}
                          onChange={(e) => setUserInfo({...userInfo, lastName: e.target.value})}
                          required
                          placeholder="Soyadınız"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>E-posta</Form.Label>
                    <Form.Control
                      type="email"
                      value={userInfo.email}
                      onChange={(e) => setUserInfo({...userInfo, email: e.target.value})}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Telefon</Form.Label>
                    <Form.Control
                      type="tel"
                      value={userInfo.phone}
                      onChange={handlePhoneChange}
                      placeholder="5XX XXX XXXX"
                    />
                  </Form.Group>

                  <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                    <Button 
                      variant="dark" 
                      type="submit"
                      disabled={loading}
                      className="px-4"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Güncelleniyor...
                        </>
                      ) : (
                        'Bilgileri Güncelle'
                      )}
                    </Button>
                  </div>
                </Form>
              </Tab.Pane>

              <Tab.Pane eventKey="orders">
                <Orders />
              </Tab.Pane>

              <Tab.Pane eventKey="password">
                <Form onSubmit={handlePasswordChange}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mevcut Şifre</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPasswords.currentPassword ? "password" : "text"}
                        value={passwordInfo.currentPassword}
                        onChange={(e) => setPasswordInfo({...passwordInfo, currentPassword: e.target.value})}
                        required
                        placeholder="Mevcut şifreniz"
                      />
                      <Button 
                        variant="outline-secondary"
                        onClick={() => togglePasswordVisibility('currentPassword')}
                        className="d-flex align-items-center justify-content-center"
                      >
                        {showPasswords.currentPassword ? <FaEye /> : <FaEyeSlash />}
                      </Button>
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Yeni Şifre</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPasswords.newPassword ? "password" : "text"}
                        value={passwordInfo.newPassword}
                        onChange={(e) => setPasswordInfo({...passwordInfo, newPassword: e.target.value})}
                        required
                        placeholder="Yeni şifreniz"
                      />
                      <Button 
                        variant="outline-secondary"
                        onClick={() => togglePasswordVisibility('newPassword')}
                        className="d-flex align-items-center justify-content-center"
                      >
                        {showPasswords.newPassword ? <FaEye /> : <FaEyeSlash />}
                      </Button>
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Yeni Şifre (Tekrar)</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPasswords.confirmPassword ? "password" : "text"}
                        value={passwordInfo.confirmPassword}
                        onChange={(e) => setPasswordInfo({...passwordInfo, confirmPassword: e.target.value})}
                        required
                        placeholder="Yeni şifrenizi tekrar girin"
                      />
                      <Button 
                        variant="outline-secondary"
                        onClick={() => togglePasswordVisibility('confirmPassword')}
                        className="d-flex align-items-center justify-content-center"
                      >
                        {showPasswords.confirmPassword ? <FaEye /> : <FaEyeSlash />}
                      </Button>
                    </InputGroup>
                  </Form.Group>

                  <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                    <Button 
                      variant="dark" 
                      type="submit"
                      disabled={loading}
                      className="px-4"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Güncelleniyor...
                        </>
                      ) : (
                        'Şifreyi Güncelle'
                      )}
                    </Button>
                  </div>
                </Form>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
}

export default Profile;